import React, { useState, useEffect } from 'react';
import { X, FileText, Info, Banknote, CreditCard, Building, Loader2, CheckCircle2, Calendar, Plus, Trash2, Clock, ShieldCheck } from 'lucide-react';
import CustomDatePicker from '../shared/CustomDatePicker';
import { recordSale } from '../../../../services/api/dailySalesReportApi';
import { getCustomerPendingInvoices } from '../../../../services/api/customerApi';
import PendingInvoicesModal from './PendingInvoicesModal';

const PAYMENT_CATEGORIES = [
    { id: 'Cash', label: 'Cash', icon: Banknote, color: 'text-emerald-600 bg-emerald-50' },
    { id: 'Credit', label: 'Credit', icon: CreditCard, color: 'text-amber-600 bg-amber-50' },
    { id: 'Bank', label: 'Bank', icon: Building, color: 'text-blue-600 bg-blue-50' }
];

const RecordSaleModal = ({ order, isOpen, onClose, onSuccess }) => {
    const [invoiceNo, setInvoiceNo] = useState('');
    const [totalOrderValue, setTotalOrderValue] = useState('');
    const [paymentCategory, setPaymentCategory] = useState('Cash');
    const [reportDate, setReportDate] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Outstanding collections state
    const [previousSales, setPreviousSales] = useState([]);
    const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
    const [pendingSummary, setPendingSummary] = useState({ count: 0, total: 0, loaded: false });

    const isEditing = Boolean(order?.recordedSale);
    const customerId = order?.customer?.publicId || order?.customerPublicId || order?.customer?.externalId || order?.customer?.id || order?.customerName || order?.customer?.name;

    useEffect(() => {
        if (order && isOpen) {
            const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
            const refNo = (order.orderRef || (order.id ? order.id.slice(-8) : '')).replace(/^#/, '');
            if (order.recordedSale) {
                const savedInv = order.recordedSale.invoiceNo;
                const cleanSavedInv = savedInv ? savedInv.replace(/^#/, '') : '';
                setInvoiceNo(cleanSavedInv && cleanSavedInv !== 'N/A' ? cleanSavedInv : '');
                const recVal = order.recordedSale.totalOrderValue;
                setTotalOrderValue(recVal ? Number(recVal).toFixed(2) : '');

                const rawCat = order.recordedSale.paymentCategory || 'CASH';
                const formattedCat = rawCat.charAt(0).toUpperCase() + rawCat.slice(1).toLowerCase();
                const matchedCat = PAYMENT_CATEGORIES.find(c => c.id.toLowerCase() === formattedCat.toLowerCase());
                setPaymentCategory(matchedCat ? matchedCat.id : 'Cash');

                const recDate = typeof order.recordedSale.reportDate === 'string'
                    ? order.recordedSale.reportDate.split('T')[0]
                    : order.recordedSale.reportDate;
                setReportDate(recDate || todayStr);
            } else {
                setInvoiceNo('');
                const calculatedTotal = order.totalAmount || (order.items || []).reduce((sum, item) => sum + (item.total || 0), 0);
                setTotalOrderValue(calculatedTotal ? Number(calculatedTotal).toFixed(2) : '');
                setPaymentCategory('Cash');
                setReportDate(todayStr);
            }
            setPreviousSales([]);
            setError(null);

            // Fetch pending summary for quick indication
            if (customerId) {
                const cleanOrderRef = (order.orderRef || (order.id ? order.id.slice(-8) : '')).replace(/^#/, '');
                getCustomerPendingInvoices(customerId)
                    .then(data => {
                        const cleanExclusions = [cleanOrderRef, invoiceNo].map(no => (no || '').replace(/^#/, '').trim().toUpperCase());
                        const list = (Array.isArray(data) ? data : [])
                            .filter(inv => {
                                const cleanInvNo = (inv.invoiceNo || '').replace(/^#/, '').trim().toUpperCase();
                                return !cleanExclusions.includes(cleanInvNo);
                            });
                        const totalSum = list.reduce((sum, inv) => sum + (Number(inv.outstandingAmount) || 0), 0);
                        setPendingSummary({ count: list.length, total: totalSum, loaded: true });
                    })
                    .catch(err => {
                        console.error('Error fetching customer pending summary:', err);
                        setPendingSummary({ count: 0, total: 0, loaded: true });
                    });
            } else {
                setPendingSummary({ count: 0, total: 0, loaded: true });
            }
        }
    }, [order, isOpen, customerId]);

    if (!isOpen || !order) return null;

    const orderRefNo = (order.orderRef || (order.id ? order.id.slice(-8) : 'N/A')).replace(/^#/, '');

    const handleAddPendingInvoices = (selectedInvoices) => {
        const existingNos = previousSales.map(s => s.invoiceNo);
        const newItems = selectedInvoices
            .filter(inv => !existingNos.includes(inv.invoiceNo))
            .map(inv => {
                const outAmt = Number(inv.outstandingAmount || 0);
                return {
                    invoiceNo: inv.invoiceNo,
                    outstandingAmount: outAmt,
                    amountReceived: outAmt.toFixed(2),
                    paymentType: 'B2B Cash'
                };
            });

        setPreviousSales(prev => [...prev, ...newItems]);
    };

    const updatePreviousSale = (index, field, value) => {
        setPreviousSales(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], [field]: value };
            return copy;
        });
    };

    const removePreviousSale = (index) => {
        setPreviousSales(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const numericAmount = parseFloat(totalOrderValue);
        if (isNaN(numericAmount) || numericAmount <= 0) {
            setError('Please enter a valid total order value');
            return;
        }

        // Validate previous sales amounts
        for (const item of previousSales) {
            const amt = parseFloat(item.amountReceived);
            if (isNaN(amt) || amt <= 0) {
                setError(`Please enter a valid amount received for invoice ${item.invoiceNo}`);
                return;
            }
        }

        setLoading(true);
        setError(null);

        const reportDateStr = reportDate || new Date().toLocaleDateString('en-CA');
        const finalInvoiceNo = invoiceNo.trim().replace(/^#/, '');
        const finalOrderNo = orderRefNo.replace(/^#/, '');

        try {
            const payload = {
                invoiceNo: finalInvoiceNo,
                orderNo: finalOrderNo,
                totalOrderValue: numericAmount,
                paymentCategory: paymentCategory.toUpperCase(),
                clientName: order.customer?.name || order.customerName || 'Customer',
                orderPublicId: order.publicId || order.id,
                salesPersonPublicId: order.assignedSalesPersonPublicId || 
                                     order.salesPersonPublicId || 
                                     order.assignedSalesPerson?.publicId || 
                                     order.delegatedSalesPerson?.publicId || 
                                     order.customer?.salesPerson?.publicId || 
                                     (order.customer?.assignedSalesPersons && order.customer.assignedSalesPersons[0]?.publicId) || 
                                     null,
                reportDate: reportDateStr,
                previousCollections: previousSales.map(item => ({
                    invoiceNo: item.invoiceNo,
                    amountReceived: parseFloat(item.amountReceived) || 0,
                    paymentType: item.paymentType === 'B2B Bank' || item.paymentType === 'Bank' ? 'B2B_BANK' : 'B2B_CASH',
                    outstandingAmount: parseFloat(item.outstandingAmount) || 0
                }))
            };

            await recordSale(payload);
            if (onSuccess) {
                onSuccess(order);
            }
            onClose();
        } catch (err) {
            console.error('Failed to record sale:', err);
            setError(err?.response?.data?.message || err.message || 'Failed to record sale to Daily Sale Sheet');
        } finally {
            setLoading(false);
        }
    };

    const selectedCategoryObj = PAYMENT_CATEGORIES.find(c => c.id === paymentCategory) || PAYMENT_CATEGORIES[0];
    const SelectedIcon = selectedCategoryObj.icon;

    return (
        <>
            <div className="fixed inset-0 z-[300] flex items-center justify-center p-3 sm:p-4 bg-gray-900/50 backdrop-blur-sm animate-in fade-in duration-200">
                <div
                    className="bg-white rounded-3xl shadow-2xl border border-gray-100 w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col animate-in zoom-in-95 duration-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-start justify-between p-4 sm:p-6 border-b border-gray-100 gap-3 shrink-0">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-sm shrink-0">
                                <FileText size={20} className="sm:hidden" />
                                <FileText size={24} className="hidden sm:block" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="text-base sm:text-xl font-bold text-gray-900 leading-tight truncate sm:whitespace-normal">
                                    {isEditing ? 'Edit Recorded Sale' : 'Record Sale & Deliver Order'}
                                </h2>
                                <p className="text-[11px] sm:text-xs font-medium text-gray-400 mt-0.5 leading-snug">
                                    {isEditing ? 'Update existing invoice details.' : 'Enter invoice details to deliver order & add to Daily Sheet.'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 sm:p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Body Form */}
                    <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto flex-1">
                        {error && (
                            <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-xs font-bold flex items-center gap-2">
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Current Order Section Header */}
                        <div className="text-xs font-black text-gray-500 uppercase tracking-wider border-b border-gray-100 pb-1">
                            Current Order
                        </div>

                        {/* Sale Date (Report Date) */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <Calendar size={14} className="text-blue-600" />
                                Sale / Report Date <span className="text-red-500">*</span>
                            </label>
                            <CustomDatePicker
                                mode="single"
                                value={reportDate}
                                onChange={(val) => setReportDate(val)}
                                quickFilters={false}
                                className="w-full"
                            />
                            <p className="text-[11px] font-medium text-gray-400 mt-1">
                                Defaults to today's date when recording delivery so it appears on today's Daily Report.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Order Reference (Read-only) */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center justify-between">
                                    <span>Order Number</span>
                                    <span className="text-[10px] font-medium text-gray-400 normal-case">(Read-only)</span>
                                </label>
                                <input
                                    type="text"
                                    value={orderRefNo}
                                    readOnly
                                    disabled
                                    className="w-full px-4 py-3 bg-gray-100/90 border border-gray-200 rounded-xl text-sm font-black text-gray-800 cursor-not-allowed select-all"
                                />
                            </div>

                            {/* Invoice Number (Optional) */}
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center justify-between">
                                    <span>Invoice Number</span>
                                    <span className="text-[10px] font-medium text-gray-400 normal-case">(Optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={invoiceNo}
                                    onChange={(e) => setInvoiceNo(e.target.value)}
                                    placeholder="Enter invoice number"
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400 placeholder:font-normal"
                                />
                            </div>
                        </div>

                        {/* Total Order Value */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                                Total Order Value (Incl. VAT) <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                                    AED
                                </span>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={totalOrderValue}
                                    onChange={(e) => setTotalOrderValue(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                                    required
                                />
                            </div>
                        </div>

                        {/* Payment Category Dropdown */}
                        <div className="relative">
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                                Payment Category <span className="text-red-500">*</span>
                            </label>

                            <button
                                type="button"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 hover:bg-gray-100/80 transition-all text-left"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-7 h-7 rounded-lg ${selectedCategoryObj.color} flex items-center justify-center`}>
                                        <SelectedIcon size={16} />
                                    </div>
                                    <span className="font-bold text-gray-800">{selectedCategoryObj.label}</span>
                                </div>
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {isDropdownOpen && (
                                <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-100 rounded-2xl shadow-xl z-20 overflow-hidden py-1 animate-in fade-in slide-in-from-top-2 duration-150">
                                    {PAYMENT_CATEGORIES.map(cat => {
                                        const CatIcon = cat.icon;
                                        const isSelected = cat.id === paymentCategory;
                                        return (
                                            <button
                                                key={cat.id}
                                                type="button"
                                                onClick={() => {
                                                    setPaymentCategory(cat.id);
                                                    setIsDropdownOpen(false);
                                                }}
                                                className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold transition-colors ${isSelected ? 'bg-blue-50/60 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-7 h-7 rounded-lg ${cat.color} flex items-center justify-center`}>
                                                        <CatIcon size={16} />
                                                    </div>
                                                    <span className="font-bold">{cat.label}</span>
                                                </div>
                                                {isSelected && <CheckCircle2 size={16} className="text-blue-600" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* ── Previous Outstanding Invoices Section ── */}
                        <div className="pt-4 border-t border-gray-200/80 space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                                            <Clock size={14} className="text-amber-600" />
                                            Previous Outstanding Invoices
                                        </h3>
                                        {/* Status Indication Badge */}
                                        {pendingSummary.loaded && (
                                            pendingSummary.count === 0 ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                    <ShieldCheck size={11} className="text-emerald-600" /> 🟢 All Clear (0 Pending)
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                                    🟡 {pendingSummary.count} Pending (AED {pendingSummary.total.toFixed(2)})
                                                </span>
                                            )
                                        )}
                                    </div>
                                    <p className="text-[11px] font-medium text-gray-400 mt-0.5">
                                        Record collections against customer's existing pending dues
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIsPendingModalOpen(true)}
                                    className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-200/80 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm active:scale-95 shrink-0 self-start sm:self-auto"
                                >
                                    <Plus size={14} /> Add Pending Invoice
                                </button>
                            </div>

                            {/* No Active Pending Invoices Banner Indication */}
                            {pendingSummary.loaded && pendingSummary.count === 0 && previousSales.length === 0 && (
                                <div className="p-3 bg-emerald-50/60 border border-emerald-200/70 rounded-2xl flex items-center justify-between text-xs text-emerald-800 font-semibold">
                                    <div className="flex items-center gap-2">
                                        <ShieldCheck size={16} className="text-emerald-600 shrink-0" />
                                        <span>No active pending invoices found for this customer.</span>
                                    </div>
                                    <span className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-lg border border-emerald-200/50">
                                        All Clear
                                    </span>
                                </div>
                            )}

                            {previousSales.length > 0 && (
                                <div className="space-y-3 bg-gray-50/80 p-3.5 rounded-2xl border border-gray-200/70">
                                    <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 pb-2 flex items-center justify-between">
                                        <span>Previous Sales Collections</span>
                                        <span>{previousSales.length} {previousSales.length === 1 ? 'Invoice' : 'Invoices'}</span>
                                    </div>

                                    {previousSales.map((item, idx) => {
                                        const outstanding = Number(item.outstandingAmount) || 0;
                                        const received = Number(item.amountReceived) || 0;
                                        const balance = outstanding - received;

                                        return (
                                            <div key={item.invoiceNo || idx} className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm space-y-3 relative group">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-black rounded-lg border border-blue-100">
                                                            {item.invoiceNo}
                                                        </span>
                                                        <span className="text-xs font-semibold text-gray-500">
                                                            Outstanding: <strong className="text-gray-900">AED {outstanding.toFixed(2)}</strong>
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removePreviousSale(idx)}
                                                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Remove"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                                                            Amount Received (AED)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={item.amountReceived}
                                                            onChange={(e) => updatePreviousSale(idx, 'amountReceived', e.target.value)}
                                                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                                                            Payment Type
                                                        </label>
                                                        <select
                                                            value={item.paymentType}
                                                            onChange={(e) => updatePreviousSale(idx, 'paymentType', e.target.value)}
                                                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                                        >
                                                            <option value="B2B Cash">▼ B2B Cash</option>
                                                            <option value="B2B Bank">▼ B2B Bank</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Remaining Balance Indicator */}
                                                <div className="flex items-center justify-between text-xs pt-1.5 border-t border-dashed border-gray-100">
                                                    <span className="font-medium text-gray-400 text-[11px]">
                                                        {balance === 0 ? (
                                                            <span className="text-emerald-600 font-bold flex items-center gap-1">
                                                                <CheckCircle2 size={13} /> Paid in Full (Balance: AED 0.00)
                                                            </span>
                                                        ) : balance > 0 ? (
                                                            <span className="text-amber-600 font-bold">
                                                                Partially Paid — Remaining Balance: AED {balance.toFixed(2)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-blue-600 font-bold">
                                                                Overpayment: AED {Math.abs(balance).toFixed(2)}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Info Callout Banner */}
                        <div className="p-3.5 bg-blue-50/60 border border-blue-100 rounded-2xl flex items-start gap-2.5 text-blue-700 text-xs">
                            <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
                            <p className="font-medium leading-relaxed">
                                {isEditing ? 'This will update the existing order record in your Daily Sale Sheet under the selected payment category.' : 'This will deliver the order, record sale value, and update your Daily Sale Sheet.'}
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="flex-1 py-3 px-4 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold shadow-lg shadow-blue-200 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 disabled:scale-100"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    isEditing ? 'Update Daily Sheet' : 'Save Details & Deliver Order'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Pending Invoices Selector Modal */}
            <PendingInvoicesModal
                customerId={customerId}
                isOpen={isPendingModalOpen}
                onClose={() => setIsPendingModalOpen(false)}
                onAddSelected={handleAddPendingInvoices}
                alreadySelectedInvoiceNos={previousSales.map(s => s.invoiceNo)}
                excludeInvoiceNos={[orderRefNo, invoiceNo]}
            />
        </>
    );
};

export default RecordSaleModal;
